import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	Form,
	FormItem,
	TextInput,
	ValidationError,
	Toolbar,
	SecretValueInput,
	FileSelector,
	Button
} from "client/Components";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	isUrl,
	hasMaxSize,
	isSupportedFileType,
	inline
} from "client/Validation/rules";
import {
	NedapOnsConnectionSaveModel,
	create,
	update,
	get,
	NedapOnsConnectionListItemModel
} from "client/Api/NedapOnsConfiguration/nedapOnsConfiguration";
import { SecretValue } from "client/Api/Apps/common";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import styles from "./NedapOnsConnectionDialog.module.less";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";

const supportedFileTypes: string[] = ["application/x-pkcs12"];
const maxFileSize: number = 100 * 1024;

export type NedapOnsConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: NedapOnsConnectionListItemModel): void;
	onClose(): void;
};

export const NedapOnsConnectionDialog = (props: NedapOnsConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		name: "",
		serverUrl: "",
		clientCertificate: {
			isSet: false
		},
		clientCertificatePassword: {
			containsNewValue: true,
			value: ""
		}
	});
	const [initialCertificateName, setInitialCertificateName] = useState<string>("");

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<FormData> = useFormHandler<FormData>(
		formData,
		setFormData,
		validationConfiguration
	);

	useEffect(
		(): (() => void) | undefined => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						serverUrl,
						clientCertificateFilename
					} = await get(
						connectionId,
						signal
					);

					setInitialCertificateName(clientCertificateFilename);

					setFormData({
						name,
						serverUrl,
						clientCertificate: { isSet: false },
						clientCertificatePassword: {
							containsNewValue: false,
							value: null
						}
					});

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			formHandler.reducer,
			onClose
		]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);
		const validationResult: InputValidationResult = await formHandler.validation
			.validate();

		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsBusy(false);
			return;
		}

		let id: number | undefined = connectionId;
		try {
			const model: NedapOnsConnectionSaveModel = prepareForSaving(formData);
			if (id === undefined) {
				id = (await create(model, globalCancellation))
					.connectionId;
			} else {
				await update(id, model, globalCancellation);
			}

			onClose();
			onAfterSave({
				id,
				name: formData.name,
				serverUrl: formData.serverUrl
			});
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
			setIsBusy(false);
		}
	};

	const enforceToSupplyCertificatePassword = (): void => {
		const clientCertificatePasswordAttachable: IValueInputAttachable<SecretValue> =
			formHandler.for("clientCertificatePassword");

		const { containsNewValue } = clientCertificatePasswordAttachable.value;
		if (containsNewValue) {
			return;
		}

		clientCertificatePasswordAttachable.reducer
			.setValue({
				containsNewValue: true,
				value: ""
			});
	};

	const enforceToSupplyCertificate = (): void => {
		const clientCertificateAttachable: IValueInputAttachable<OptionalModel<Base64FileUriUploadWithMetadataModel | null>> =
			formHandler.for("clientCertificate");

		const { isSet } = clientCertificateAttachable.value;
		if (isSet) {
			return;
		}

		clientCertificateAttachable.reducer
			.setValue({
				isSet: true,
				value: null
			});
	};

	const clientCertificateValue: OptionalModel<Base64FileUriUploadWithMetadataModel | null> =
		formHandler.for("clientCertificate")
			.value;

	return (
		<Dialog hasBusyIndicator={isBusy} trackingId="nedapons-connection-dialog">
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_NedapOns")}
						]}
					/>
				}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Name")}>
						<TextInput
							{...formHandler.for("name")
								.attachToTextInput()
							}
						/>
						<ValidationError
							{...formHandler.for("name")
								.attachToValidationError()
							}
						/>
					</FormItem>
					<FormItem label={localize("_ApiUrl")}>
						<TextInput
							{
								...formHandler.for("serverUrl")
									.attachToTextInput()
									.withOnChange(()=> {
										enforceToSupplyCertificatePassword();
										enforceToSupplyCertificate();
									})
							}
						/>
						<ValidationError
							{...formHandler.for("serverUrl")
								.attachToValidationError()
							}
						/>
					</FormItem>
					<FormItem label={localize("_Certificate")}>
						<FileSelector
							{...formHandler.for("clientCertificate")
								.attachToFileSelector()
								.withOnChange(enforceToSupplyCertificatePassword)
							}
							contentTypes={supportedFileTypes}
						>
							{onClick => (
								<div>
									<Button
										kind="secondary"
										text={localize("_SelectFile")}
										onClick={onClick}
										trackingId="add-certificate"
									/>
									<span className={styles.fileName}>
										{clientCertificateValue.isSet === true
												? clientCertificateValue.value?.filename
													?? ""
												: initialCertificateName
										}
									</span>
								</div>
							)}
						</FileSelector>
						<ValidationError
							{...formHandler.for("clientCertificate")
								.attachToValidationError()
							}
						/>
						{formHandler.for("clientCertificate")
							.whenType(
								isCertificateSet,
								(attachable) => {
									return (
										<>
											<ValidationError
												{...attachable.for("value")
													.attachToValidationError()
												}
											/>
											<ValidationError
												{...attachable.for("value")
													.for("filename")
													.attachToValidationError()
												}
											/>
											<ValidationError
												{...attachable.for("value")
													.for("uri")
													.attachToValidationError()
												}
											/>
										</>
									);
								}
							)
						}
					</FormItem>

					<FormItem label={localize("_CertificatePassword")}>
						<SecretValueInput
							{...formHandler.for("clientCertificatePassword")
								.attachToSecretValueInput()
								.withOnChange(enforceToSupplyCertificate)
							}
							containsInitialValue={!isNewConnection}
						/>
						<ValidationError
							{...formHandler.for("clientCertificatePassword")
								.for("value")
								.attachToValidationError()
							}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveKey)}
						onClick={submitHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NedapOnsConnectionDialog.displayName = "NedapOnsConnectionDialog";

type FormData = OverrideProperties<
	NedapOnsConnectionSaveModel,
	{
		clientCertificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
	}
>;

function isCertificateSet(clientCertificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>)
	: clientCertificate is SetOptionalModel<Base64FileUriUploadWithMetadataModel> {
	return clientCertificate.isSet === true
		&& isNotNil(clientCertificate.value);
}

function prepareForSaving(model: FormData): NedapOnsConnectionSaveModel {
	const prepareCertificate = (certificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>): OptionalModel<Base64FileUriUploadModel> => {
		if (!certificate.isSet) {
			return { isSet: false };
		}

		const {
			filename,
			uri
		} = ensureNotNil(certificate.value);

		return {
			isSet: true,
			value: {
				filename,
				uri
			}
		};
	};

	return {
		...model,
		clientCertificate: prepareCertificate(model.clientCertificate)
	};
}

const validationConfiguration: IValidationConfiguration<FormData> =
	createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("serverUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true,
			allowQueryString: false
		})
	);

validationConfiguration.forScalar("clientCertificate")
	.add(
		hasMaxSize(maxFileSize),
		isSupportedFileType(supportedFileTypes),
		inline(
			(file: OptionalModel<Base64FileUriUploadWithMetadataModel | null>): InputValidationResult => {
				if (file.isSet && file.value === null) {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_IsRequired")
					};
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
);

validationConfiguration.forObject("clientCertificatePassword")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);
